import { COLUMNS, columnIndexOf, pad } from '@bingo/shared';

/**
 * A bola. Mesma estrutura do protótipo (`ballHTML` / `#big`):
 * `.ball > .face > .l + .n`, com `--c` definindo a cor da coluna.
 */

export function ballColor(ball: number | null | undefined): string {
  if (!ball) return '#4a4390';
  return COLUMNS[columnIndexOf(ball)]?.color ?? '#4a4390';
}

interface BallProps {
  ball: number | null;
  className?: string;
  /** Rótulo mostrado durante o suspense, quando o número ainda é provisório. */
  id?: string;
}

export function Ball({ ball, className = '', id }: BallProps) {
  if (!ball) {
    return (
      <div className={`ball empty ${className}`.trim()} id={id}>
        <div className="face">
          <span className="l">&nbsp;</span>
          <span className="n">–</span>
        </div>
      </div>
    );
  }

  const letter = COLUMNS[columnIndexOf(ball)]?.letter ?? '';

  return (
    <div
      className={`ball ${className}`.trim()}
      id={id}
      style={{ '--c': ballColor(ball) } as React.CSSProperties}
    >
      <div className="face">
        <span className="l">{letter}</span>
        <span className="n">{pad(ball)}</span>
      </div>
    </div>
  );
}

/** A bola grande do palco, com as animações do protótipo. */
export function BigBall({
  ball,
  animation,
  ariaLive = 'polite',
}: {
  ball: number | null;
  animation: 'roll' | 'shake' | 'flash' | null;
  ariaLive?: 'polite' | 'off';
}) {
  const letter = ball ? (COLUMNS[columnIndexOf(ball)]?.letter ?? '') : ' ';

  return (
    <div
      className={['ball', ball ? '' : 'empty', animation ?? ''].filter(Boolean).join(' ')}
      id="big"
      aria-live={ariaLive}
      style={ball ? ({ '--c': ballColor(ball) } as React.CSSProperties) : undefined}
    >
      <div className="face">
        <span className="l">{letter}</span>
        <span className="n">{ball ? pad(ball) : '?'}</span>
      </div>
    </div>
  );
}

/** Bola pequena do painel de letras (B, I, N, G, O). */
export function LetterBall({ letter, color }: { letter: string; color: string }) {
  return (
    <div className="ball letter" style={{ '--c': color } as React.CSSProperties}>
      <div className="face">
        <span className="n">{letter}</span>
      </div>
    </div>
  );
}
